import { useState } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";import { ArrowRight, ArrowDown } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];
const LINES = [
  { text: "Your customer sees", serif: false },
  { text: "the finished system.", serif: true },
];
const LABELS = ["COMPLEX GEOMETRY", "CLOSE TOLERANCES", "DEFINED REQUIREMENTS"];

const goTo = (id) => (e) => {
  e.preventDefault();
  const el = document.querySelector(id);
  if (window.__lenis && el) window.__lenis.scrollTo(el, { offset: -90 });
  else el?.scrollIntoView({ behavior: "smooth" });
};

// Stage 3 copy (C) — revealed once the aircraft settles at center (progress ~0.72).
export const HeroCopy = ({ trackRef, reduced, onToggleMotion }) => {
  const { scrollYProgress } = useScroll({ target: trackRef, offset: ["start start", "end end"], layoutEffect: false });
  const p = scrollYProgress;
  const [show, setShow] = useState(false);
  useMotionValueEvent(p, "change", (v) => setShow(v > 0.705));

  const contO = useTransform(p, [0.7, 0.76], [0, 1]);
  const contV = useTransform(p, (v) => (v > 0.695 ? "visible" : "hidden"));
  const topY = useTransform(p, [0.72, 1], [0, reduced ? 0 : -70]);
  const botY = useTransform(p, [0.72, 1], [26, reduced ? 0 : -30]);

  const rise = (delay) => ({
    initial: reduced ? false : { opacity: 0, y: 18 },
    animate: show ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 },
    transition: { duration: 1, delay: show ? delay : 0, ease: EASE },
  });

  const Cta = ({ href, testId, primary, children }) => (
    <a
      href={href}
      onClick={goTo(href)}
      data-testid={testId}
      className={`btn ${primary ? "btn-light" : "btn-ghost"}`}
    >
      {children}
      {primary && <ArrowRight className="h-4 w-4" />}
    </a>
  );

  return (
    <motion.div style={{ opacity: contO, visibility: contV }} className="pointer-events-none absolute inset-0 z-20">
      <motion.div style={{ y: topY }} className="pointer-events-auto absolute left-0 top-0 px-5 pt-[5.5rem] sm:px-8 lg:px-12 lg:pt-28">
        <motion.p {...rise(0.3)} className="eyebrow mb-4 !text-[#FFC48C]">
          <span className="rule !bg-[#FFC48C]" />
          PRECISION IN EVERY DIMENSION
        </motion.p>
        <h1 data-testid="hero-headline" className="headline text-[2.6rem] text-white sm:text-6xl lg:text-7xl xl:text-[5.2rem]">
          {LINES.map((line) => (
            <span key={line.text} className="block overflow-hidden pb-[0.08em]">
              <motion.span
                className="block"
                initial={reduced ? false : { y: "108%" }}
                animate={show ? { y: "0%" } : { y: "108%" }}
                transition={{ duration: 1.25, delay: show ? 0.25 : 0, ease: EASE }}
              >
                {line.serif ? <span className="font-serif-i">{line.text}</span> : line.text}
              </motion.span>
            </span>
          ))}
        </h1>
      </motion.div>

      <motion.div style={{ y: botY }} className="pointer-events-auto absolute inset-x-0 bottom-0 flex items-end justify-between gap-8 px-5 pb-24 sm:px-8 sm:pb-24 lg:px-12 lg:pb-16">
        <motion.div {...rise(1.0)} className="hidden items-center gap-5 md:flex">
          <span data-testid="scroll-indicator" className="flex items-center gap-3 font-mono text-[11px] tracking-[0.22em] text-zinc-200">
            <span className="relative flex h-9 w-9 items-center justify-center rounded-full border border-white/25">
              <ArrowDown className={`h-3.5 w-3.5 ${reduced ? "" : "animate-scroll-cue"}`} />
            </span>
            KEEP SCROLLING
          </span>
          <MotionToggle reduced={reduced} onToggle={onToggleMotion} />
        </motion.div>

        <div className="w-full max-w-[30rem] md:w-auto">
          <motion.p {...rise(0.75)} data-testid="hero-supporting-text" className="text-[15px] leading-relaxed text-white sm:text-lg sm:leading-[1.45] [text-wrap:pretty]">
            You know how much depends on every part.
          </motion.p>
          <motion.div {...rise(0.9)} className="mt-4 flex flex-wrap gap-2">
            {LABELS.map((l) => (
              <span key={l} className="chip !border-white/25 !bg-white/10">{l}</span>
            ))}
          </motion.div>
          <motion.div {...rise(1.05)} className="mt-5 flex flex-wrap gap-2.5 sm:gap-3">
            <Cta href="#contact" testId="request-quote-button" primary>Request a Quote</Cta>
            <Cta href="#capabilities" testId="explore-capabilities-button">Explore Our Capabilities</Cta>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const MotionToggle = ({ reduced, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    data-testid="reduced-motion-toggle"
    aria-pressed={reduced}
    className="shrink-0 rounded-full border border-white/20 px-3 py-1.5 font-mono text-[11px] tracking-[0.14em] text-zinc-200 transition-colors duration-300 hover:bg-white/10"
  >
    {reduced ? "MOTION OFF" : "MOTION ON"}
  </button>
);
